import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import type { CustomRendererComponentProps, ToolDefinition, ToolExecutionContext } from '../types';

import { EditChartTool } from './EditChartTool';
import { computeSceneShortIds } from './scene-addressing';
import { resolveInsertSlotAfter } from './scene-group-addressing';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import {
    applyGeneratedChartConfigOperation,
    getDefaultChartConfig,
    setupChartSceneOperation,
} from 'js/src/libs/chart-asset-add';
import { SUPPORTED_CHART_TYPES, type SupportedChartType } from 'js/src/libs/default-chart-configs';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { addBlankSceneOperation } from 'js/src/luminary/luminary-operations';

interface AddChartSceneArgs {
    chart_type: SupportedChartType;
    data: string;
    after_scene_group_id?: string;
}

export class AddChartSceneTool extends VideoStateTool<AddChartSceneArgs> {
    public static readonly toolName = 'add_chart_scene';

    public override customRenderer = AddChartSceneResultRenderer;

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AddChartSceneTool.toolName,
                description: `Insert a NEW dedicated chart scene — a full-scene chart on a clean background — and populate it with data ("add a chart / show this data as a chart"). It creates its own clean scene with the chart front-and-center, so it never buries existing scene text. After adding, verify with a screenshot and fix a wrong label, value, or type with ${EditChartTool.toolName}.`,
                parameters: {
                    type: 'object',
                    properties: {
                        chart_type: {
                            type: 'string',
                            description: 'The kind of chart to create.',
                            enum: [...SUPPORTED_CHART_TYPES],
                        },
                        data: {
                            type: 'string',
                            description:
                                'A description of what the chart should show, with any actual numbers/labels included inline (e.g. "coin-flip outcomes — Heads 6, Tails 4" or "quarterly revenue: Q1 1.2M, Q2 1.8M"). Include figures when you have them; otherwise describe the data and the generator supplies it.',
                        },
                        after_scene_group_id: {
                            type: 'string',
                            description:
                                'Optional stable scene_group_id of the scene group the chart scene should be inserted immediately after. Omit to add it at the end of the video (the usual case).',
                        },
                    },
                    required: ['chart_type', 'data'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Add Chart Scene';
    }

    public get statusIcon(): string {
        return 'bar_chart';
    }

    public async execute(args: AddChartSceneArgs, context: ToolExecutionContext) {
        const { chart_type, data } = args;
        const { parentActionGroup } = context;

        try {
            // Unlike the other id-anchored inserts, add_chart_scene defaults to the END of the video
            // (a chart is usually a closing/standalone scene), so an omitted anchor means append —
            // not the shared "insert at start" default. A given anchor still means "immediately after".
            const sceneGroupCount = this.luminary.sceneGroups.length;
            const targetIndex =
                args.after_scene_group_id == null || args.after_scene_group_id.trim() === ''
                    ? sceneGroupCount
                    : resolveInsertSlotAfter(this.luminary, args.after_scene_group_id);

            const config = getDefaultChartConfig(chart_type, 0);
            if (!config) {
                return `Error: No default chart config for type ${chart_type}`;
            }

            const { addBlankScene, setupChartScene, applyGeneratedChartConfig } =
                makeOperationsCallable(
                    {
                        addBlankSceneOperation,
                        setupChartSceneOperation,
                        applyGeneratedChartConfigOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    this._getState as () => RootState,
                );

            const { sceneId: blankSceneId } = addBlankScene(
                targetIndex,
                false,
                parentActionGroup,
                false,
            );

            const setup = setupChartScene(blankSceneId, chart_type, config);
            if (!setup) {
                const blankSceneShortId =
                    computeSceneShortIds(this.luminary.scenes.map((s) => s.id)).get(blankSceneId) ??
                    blankSceneId;
                return `Error: Could not set up chart scene ${blankSceneShortId}`;
            }

            const { sceneId, assetId } = setup;

            const status = await applyGeneratedChartConfig({
                sceneId,
                assetId,
                chartType: chart_type,
                data,
                extractChartDataFromResult: true,
                parentActionGroup,
            });

            // setupChartScene doesn't checkpoint, and applyGeneratedChartConfig only checkpoints
            // on success — so checkpoint here when data generation didn't.
            if (status !== 'success') {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            const sceneShortId =
                computeSceneShortIds(this.luminary.scenes.map((s) => s.id)).get(sceneId) ?? sceneId;

            if (status === 'success') {
                return `Success: Added a ${chart_type} chart scene (scene_id: ${sceneShortId}) and populated it with data.`;
            }
            return `Success: Added a ${chart_type} chart scene (scene_id: ${sceneShortId}), but data generation failed so the chart shows placeholder data. Use ${EditChartTool.toolName} to set the data.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding chart scene: ${errorMessage}`;
        }
    }
}

const AddChartSceneResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
        </div>
    );
};
